import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Target, Heart, Users, Lightbulb, Store, ShoppingBag, CalendarDays } from "lucide-react";

const VALUES = [
  { icon: Target, title: "Our Mission", desc: "To strengthen the local economy by connecting Columbus shoppers with small businesses and markets in their communities." },
  { icon: Heart, title: "Community First", desc: "We believe in the power of community. Every purchase from a local business keeps money circulating in Columbus." },
  { icon: Users, title: "Inclusive Platform", desc: "We welcome all small businesses and markets throughout the Columbus and surrounding areas to join our growing directory." },
  { icon: Lightbulb, title: "Innovation", desc: "We're constantly evolving our platform to better serve both businesses and shoppers with new tools and features." }
];

const AboutVisual = () => (
  <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-xl">
    <div className="mb-5 flex items-center justify-between">
      <div>
        <div className="h-3 w-32 rounded-full bg-[#1a1a1a] mb-2" />
        <div className="h-2 w-44 rounded-full bg-gray-200" />
      </div>
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#42D78A]/10">
        <Heart className="h-6 w-6 text-[#42D78A]" />
      </div>
    </div>
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      {[
        { icon: ShoppingBag, label: "Shoppers" },
        { icon: Store, label: "Businesses" },
        { icon: CalendarDays, label: "Markets" },
      ].map((item) => (
        <div key={item.label} className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-white shadow-sm">
            <item.icon className="h-6 w-6 text-[#42D78A]" />
          </div>
          <p className="text-xs font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.label}</p>
        </div>
      ))}
    </div>
    <div className="mt-5 rounded-xl border border-gray-200 p-4">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-xs font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>Local economy loop</span>
        <span className="rounded-full bg-[#42D78A]/10 px-3 py-1 text-[10px] font-bold text-[#1a1a1a]">614 Hub</span>
      </div>
      <div className="h-2 w-full rounded-full bg-gray-200 mb-2" />
      <div className="h-2 w-3/4 rounded-full bg-gray-200" />
    </div>
  </div>
);

const AboutPage = () => {
  return (
    <div className="page-fade-in">
      {/* Hero */}
      <section className="bg-[#EEF8F2] text-[#1a1a1a] border-b border-[#42D78A]/20 py-20 md:py-28 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#42D78A] text-sm tracking-[0.2em] uppercase mb-4 font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>About Us</p>
          <h1 className="text-4xl sm:text-5xl font-bold mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Building a Stronger Columbus,<br />One Shop at a Time
          </h1>
          <p className="text-gray-600 text-base md:text-lg max-w-2xl mx-auto leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            614 Shop Local is a community driven platform, made to help support the local businesses & markets within the Columbus and surrounding areas.
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold text-[#1a1a1a] mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>Our Story</h2>
            <p className="text-gray-600 mb-4 leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Founded with the belief that local businesses are the heartbeat of any community, 614 Shop Local was created to give Columbus-area small businesses and markets the visibility they deserve.
            </p>
            <p className="text-gray-600 mb-6 leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              We know that when you shop local, your dollars stay local. Every purchase supports a neighbor, funds a dream, and strengthens the fabric of our community. Our platform makes it easy to discover, connect with, and support the amazing businesses right in your backyard.
            </p>
            <Link to="/register">
              <Button className="bg-[#42D78A] hover:bg-[#3bc27c] text-[#1a1a1a] rounded-full px-8 py-5 text-sm font-bold transition-all hover:-translate-y-0.5" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="about-join-btn">
                Join Our Community
              </Button>
            </Link>
          </div>
          <AboutVisual />
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-gray-50 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-[#1a1a1a] mb-14" style={{ fontFamily: "'Outfit', sans-serif" }}>
            What Drives Us
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 stagger-in">
            {VALUES.map((v) => (
              <div key={v.title} className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 rounded-full bg-[#42D78A]/10 flex items-center justify-center mb-4">
                  <v.icon className="w-6 h-6 text-[#42D78A]" />
                </div>
                <h3 className="text-lg font-bold text-[#1a1a1a] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{v.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif" }}>{v.desc}</p>
              </div>
            ))}
          </div>
          <div className="max-w-3xl mx-auto text-center mt-14">
            <p className="text-gray-600 mb-5 leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              614 Shop Local is part of The Shop Local Collective, which aims to build a digital ecosystem that helps create a stronger local small business economy. 614 Shop Local is the first trial hub for this work.
            </p>
            <a
              href="https://theshoplocalcollective.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex text-sm font-bold text-[#1a1a1a] underline decoration-[#42D78A] decoration-2 underline-offset-4 hover:text-[#2fbf78] transition-colors"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Learn more about The Shop Local Collective
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
